import React, { useEffect, useState } from 'react'
import {
  Box,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material'
import { columns } from './ListColumns'
import { useParams } from 'react-router-dom'
import { useGetProject } from '../hooks'
import { CostCodesGrid } from './CostCodesGrid'
import { BackButton } from '../../../components/navigation/BackButton'
import { useGetCostCodes } from './hooks'

const ListCostCodes = (props: any) => {
  const { id } = useParams()
  const { data: projectData } = useGetProject('project', id)
  const { data: costCodesData } = useGetCostCodes(
    'costcodes',
    id,
    { page: 1, pageSize: 500 },
    {}
  )
  const [hierarchyLevels, setHierarchyLevels] = useState<any[]>([])
  const [selectedCodes, setSelectedCodes] = useState<string[]>([])
  const [maxDepth, setMaxDepth] = useState(0)

  const calculateMaxDepth = (data: any[]): number => {
    let max = 0
    const checkDepth = (items: any[], currentDepth: number) => {
      items.forEach(item => {
        max = Math.max(max, currentDepth)
        if (item.children && item.children.length > 0) {
          checkDepth(item.children, currentDepth + 1)
        }
      })
    }
    checkDepth(data, 1)
    return max
  }

  useEffect(() => {
    if (costCodesData && costCodesData.hierarchicalData) {
      setHierarchyLevels(costCodesData.hierarchicalData)
      setSelectedCodes([])
      // Calculate max depth
      const depth = calculateMaxDepth(costCodesData.hierarchicalData)
      setMaxDepth(depth)
    }
  }, [costCodesData])

  const handleSelectChange = (
    event: React.ChangeEvent<{ value: unknown }>,
    level: number
  ) => {
    const newSelectedCodes = [
      ...selectedCodes.slice(0, level),
      event.target.value as string,
    ]
    setSelectedCodes(newSelectedCodes)
  }

  const getFilteredOptions = (level: number) => {
    let currentLevel = hierarchyLevels
    for (let i = 0; i < level; i++) {
      if (!selectedCodes[i]) break
      const found = currentLevel.find(item => item.code === selectedCodes[i])
      currentLevel = found?.children || []
    }
    return currentLevel.map((item: any) => ({
      _id: item._id,
      code: item.code,
      description: item.description,
    }))
  }

  const getLevelLabel = (level: number) => {
    const labels = ['Floor', 'Area', 'Task', 'Subtask', 'Detail']
    return labels[level] || `Level ${level + 1}`
  }

  return (
    <Box sx={{ marginTop: 1, marginLeft: 2, marginRight: 2 }}>
      <Container maxWidth='lg'>
        <BackButton />
        <Typography variant='h5' gutterBottom>
          {projectData
            ? projectData.projectName + ' - Manage Cost Codes'
            : 'Cost Codes'}
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          {Array.from({ length: maxDepth }, (_, level) => {
            if (level === 0 || selectedCodes[level - 1]) {
              return (
                <FormControl
                  key={level}
                  sx={{ minWidth: 200, maxWidth: 300, width: '30%' }}>
                  <InputLabel>{getLevelLabel(level)}</InputLabel>
                  <Select
                    value={selectedCodes[level] || ''}
                    onChange={(event: any) => handleSelectChange(event, level)}
                    label={getLevelLabel(level)}>
                    <MenuItem value=''>
                      <em>None</em>
                    </MenuItem>
                    {getFilteredOptions(level).map((item: any) => (
                      <MenuItem
                        key={`${item._id}-${item.code}`}
                        value={item.code}>
                        {item.code}: {item.description}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )
            }
            return null
          })}
        </Box>

        <Box sx={{ flex: 2 }}>
          <CostCodesGrid
            id={id}
            columns={columns}
            uploadPath='upload'
            costCodes={costCodesData?.costCodes || []}
          />
        </Box>
      </Container>
    </Box>
  )
}

export default ListCostCodes
